'use client';

import { useEffect, useRef, useState } from 'react';
import {
  anonymizeFullStoryUser,
  identifyFullStoryUser,
  initializeFullStory,
} from 'src/helper/fullstory';
import { runWhenIdle } from 'src/helper/run-when-idle';
import { useAppSelector } from 'src/hooks/redux-hooks';

/**
 * Initializes FullStory on mount and identifies/anonymizes the user
 * when auth state changes. Must be rendered inside the Redux Provider.
 */
export default function FullStoryInit() {
  const userDetails = useAppSelector((state) => state.profile.app.userDetails.data);
  const previousUserIdRef = useRef<string | null>(null);
  const [isFullStoryStarted, setIsFullStoryStarted] = useState(false);

  // Deferred so fs.js doesn't compete with the boot requests and first render.
  useEffect(
    () =>
      runWhenIdle(() => {
        void initializeFullStory();
        setIsFullStoryStarted(true);
      }),
    [],
  );

  useEffect(() => {
    // Identify is a no-op before init starts, so wait for it.
    if (!isFullStoryStarted) return;

    const currentUserId = userDetails?.user_id ?? null;

    // Skip if user hasn't changed
    if (currentUserId === previousUserIdRef.current) return;

    previousUserIdRef.current = currentUserId;

    if (currentUserId && userDetails) {
      void identifyFullStoryUser(userDetails);
    } else {
      void anonymizeFullStoryUser();
    }
  }, [isFullStoryStarted, userDetails]);

  return null;
}
